import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { SxProps, useTheme } from '@mui/material/styles'
import React, { MouseEventHandler } from 'react'
import { getButtonTestId } from '../Buttons/getButtonTestId'

/** @notExported */
interface CaleoIconButtonProps {
  /** Action to perform when button is clicked. */
  clickAction?: MouseEventHandler<unknown>
  /** Tooltip for button. */
  tooltip?: string | React.ReactNode
  /** Whether or not the button is valid. */
  valid?: boolean
  /** Size of the button. */
  size?: 'medium' | 'small' | undefined | 'xsmall' | 'large'
  /** Icon to display. */
  icon: React.ReactNode
  /** Whether or not the button should have a colored background. */
  coloredBackground?: boolean
  /** Additional styles to apply to the button. */
  sx?: SxProps
  /** Test id of the button. */
  testId?: string
  /** Accessible label for screen readers when tooltip is not a plain string. */
  ariaLabel?: string
}

/**
 * Caleo icon button component.
 *
 * @returns Caleo icon button component.
 * @notExported
 * @category Buttons
 */
const CaleoIconButton: React.FC<CaleoIconButtonProps> = ({
  clickAction,
  tooltip,
  valid = true,
  size = 'small',
  icon,
  coloredBackground,
  sx,
  testId,
  ariaLabel,
}) => {
  const theme = useTheme()
  const label = typeof tooltip === 'string' ? tooltip : (ariaLabel ?? 'icon button')
  const resolvedTestId = getButtonTestId({ fallback: 'button-caleo-icon', label, testId })

  const getButton = () => {
    return (
      <IconButton
        aria-label={label}
        data-testid={resolvedTestId}
        sx={Object.assign(
          coloredBackground
            ? {
                float: 'right',
                border: `1px solid ${theme.palette.secondary.main}`,
                background: theme.palette.secondary.light,
              }
            : {
                float: 'right',
              },
          sx ?? {}
        )}
        onClick={clickAction}
        disabled={!valid}
        size={size === 'xsmall' ? 'medium' : size}
      >
        {icon}
      </IconButton>
    )
  }
  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip} describeChild>
          {!valid ? <>{getButton()}</> : getButton()}
        </Tooltip>
      ) : (
        getButton()
      )}
    </>
  )
}

export default CaleoIconButton
